import React, { useEffect, useRef, useState } from "react";

/**
 * Reveal — animates children into view on scroll using IntersectionObserver.
 * props:
 *  - as: wrapper element (default "div")
 *  - delay: ms delay before animating
 *  - y: vertical offset in px (default 28)
 *  - once: animate only the first time (default true)
 */
const Reveal = ({ children, as: Tag = "div", delay = 0, y = 28, once = true, className = "" }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          if (once) obs.unobserve(entry.target);
        } else if (!once) {
          setShown(false);
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [once]);

  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        transition: "opacity 0.8s cubic-bezier(0.22,1,0.36,1), transform 0.8s cubic-bezier(0.22,1,0.36,1)",
        transitionDelay: `${delay}ms`,
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : `translateY(${y}px)`,
        willChange: "opacity, transform",
      }}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
